import React, { useMemo, useState } from 'react';
import { Row, Col, Button, DatePicker, Select, Segmented, Alert } from 'antd';
import { LineChartOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import PageContainer from '@/components/PageContainer';
import ChartCard from '@/components/ChartCard';
import { useWorkbenchMetrics, useWorkbenchData } from '@/api/workbench';
import { BASE_DATAZOOM, format2 } from '@/utils/charts';
import type { EChartsOption } from 'echarts';

const TAG_OPTIONS = ['全部', '日前', '实时', '预测', '实际'];

const CHART_TYPE_OPTIONS = [
  { label: '折线图', value: 'line' },
  { label: '柱状图', value: 'bar' },
];

const AGG_OPTIONS = [
  { label: '15分钟', value: '15T' },
  { label: '1小时', value: '1H' },
  { label: '1天', value: '1D' },
];

function extractUnit(name: string): string {
  const m = name.match(/\(([^)]+)\)/);
  return m ? m[1] : '';
}

const DEFAULT_Y1 = '[公有-现货供需] 实际竞价空间 (MW)';
const DEFAULT_Y2 = '[公有-现货电价] 实时出清均价 (元/MWh)';

const WorkbenchPage: React.FC = () => {
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [aggLevel, setAggLevel] = useState('1H');
  const [y1Type, setY1Type] = useState('line');
  const [y2Type, setY2Type] = useState('line');
  const [y1Tag, setY1Tag] = useState('全部');
  const [y2Tag, setY2Tag] = useState('全部');
  const [y1Metrics, setY1Metrics] = useState<string[]>([DEFAULT_Y1]);
  const [y2Metrics, setY2Metrics] = useState<string[]>([DEFAULT_Y2]);

  const {
    data: metricsData,
    error: metricsError,
  } = useWorkbenchMetrics();
  const categories = metricsData?.data ?? [];

  const allMetricNames = useMemo(() => {
    const names: string[] = [];
    for (const cat of categories) {
      for (const m of cat.metrics) {
        names.push(`[${cat.name}] ${m.name}`);
      }
    }
    return names;
  }, [categories]);

  const y1Options = useMemo(() => {
    if (y1Tag === '全部') return allMetricNames;
    const filtered: string[] = [];
    for (const cat of categories) {
      for (const m of cat.metrics) {
        if (m.tag === y1Tag) {
          filtered.push(`[${cat.name}] ${m.name}`);
        }
      }
    }
    return filtered;
  }, [allMetricNames, categories, y1Tag]);

  const y2Options = useMemo(() => {
    if (y2Tag === '全部') return allMetricNames;
    const filtered: string[] = [];
    for (const cat of categories) {
      for (const m of cat.metrics) {
        if (m.tag === y2Tag) {
          filtered.push(`[${cat.name}] ${m.name}`);
        }
      }
    }
    return filtered;
  }, [allMetricNames, categories, y2Tag]);

  const startDateStr = dates[0]?.format('YYYY-MM-DD') ?? '';
  const endDateStr = dates[1]?.format('YYYY-MM-DD') ?? '';

  const {
    data: wbData,
    isLoading,
    error,
    refetch,
  } = useWorkbenchData({
    start_date: startDateStr,
    end_date: endDateStr,
    y1_metrics: y1Metrics,
    y2_metrics: y2Metrics,
    agg_level: aggLevel,
  });

  const unitError = useMemo(() => {
    const u1 = new Set(y1Metrics.map(extractUnit));
    const u2 = new Set(y2Metrics.map(extractUnit));
    if (u1.size > 1) return `Y1 轴包含单位: ${Array.from(u1).join(', ')}，请确保同一轴单位一致。`;
    if (u2.size > 1) return `Y2 轴包含单位: ${Array.from(u2).join(', ')}，请确保同一轴单位一致。`;
    return '';
  }, [y1Metrics, y2Metrics]);

  const canQuery = !!startDateStr && !!endDateStr && (y1Metrics.length + y2Metrics.length) > 0 && !unitError;

  // Chart data preparation
  const chartOption: EChartsOption = useMemo(() => {
    const raw = wbData?.data ?? {};
    const allTimes = new Set<string>();
    for (const key of Object.keys(raw)) {
      for (const row of raw[key]) {
        if (row.time) allTimes.add(row.time);
      }
    }
    const sortedTimes = Array.from(allTimes).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    const colorPalette = ['#0d9488', '#52c41a', '#faad14', '#f5222d', '#13c2c2', '#722ed1', '#eb2f96', '#fa8c16'];

    const series: EChartsOption['series'] = [];
    let colorIdx = 0;

    for (const m of y1Metrics) {
      const data = raw[m] ?? [];
      const timeMap = new Map(data.map((r) => [r.time, r.val]));
      series.push({
        name: m,
        type: y1Type as 'line' | 'bar',
        yAxisIndex: 0,
        data: sortedTimes.map((t) => timeMap.get(t) ?? null),
        smooth: true,
        showSymbol: false,
        connectNulls: true,
        itemStyle: { color: colorPalette[colorIdx % colorPalette.length] },
        lineStyle: y1Type === 'line' ? { width: 2 } : undefined,
      });
      colorIdx++;
    }

    for (const m of y2Metrics) {
      const data = raw[m] ?? [];
      const timeMap = new Map(data.map((r) => [r.time, r.val]));
      series.push({
        name: m,
        type: y2Type as 'line' | 'bar',
        yAxisIndex: 1,
        data: sortedTimes.map((t) => timeMap.get(t) ?? null),
        smooth: true,
        showSymbol: false,
        connectNulls: true,
        itemStyle: { color: colorPalette[colorIdx % colorPalette.length] },
        lineStyle: y2Type === 'line' ? { width: 2 } : undefined,
      });
      colorIdx++;
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        valueFormatter: (value: any) => typeof value === 'number' ? format2(value) : value,
      },
      toolbox: { show: false },
      dataZoom: BASE_DATAZOOM,
      legend: { bottom: 50, type: 'scroll' as const },
      grid: { left: 50, right: 50, top: 30, bottom: 110 },
      xAxis: {
        type: 'category',
        data: sortedTimes.map((t) => dayjs(t).format('MM-DD HH:mm')),
        axisLabel: { fontSize: 11, color: '#9ca3af' },
      },
      yAxis: [
        {
          type: 'value',
          name: y1Metrics.length > 0 ? extractUnit(y1Metrics[0]) : '',
          axisLabel: { fontSize: 11, color: '#9ca3af' },
          splitLine: { lineStyle: { color: '#f3f4f6' } },
        },
        {
          type: 'value',
          name: y2Metrics.length > 0 ? extractUnit(y2Metrics[0]) : '',
          axisLabel: { fontSize: 11, color: '#9ca3af' },
          splitLine: { show: false },
        },
      ],
      series,
    };
  }, [wbData, y1Metrics, y2Metrics, y1Type, y2Type]);

  const handleExportCsv = () => {
    const raw = wbData?.data ?? {};
    const allTimes = new Set<string>();
    for (const key of Object.keys(raw)) {
      for (const row of raw[key]) {
        if (row.time) allTimes.add(row.time);
      }
    }
    const sortedTimes = Array.from(allTimes).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const metrics = [...y1Metrics, ...y2Metrics];
    const rows = sortedTimes.map((t) => {
      const r: Record<string, string | number | null> = { 时间: t };
      for (const m of metrics) {
        const found = raw[m]?.find((x) => x.time === t);
        r[m] = found?.val ?? '';
      }
      return r;
    });

    const headers = ['时间', ...metrics];
    const csvLines = [
      '﻿' + headers.join(','),
      ...rows.map((r) => headers.map((h) => String(r[h] ?? '')).join(',')),
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    let url: string | null = null;
    try {
      url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workbench_export_${startDateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      if (url) URL.revokeObjectURL(url);
    }
  };

  return (
    <PageContainer title="交叉分析工作台">
      {/* Control bar */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>日期范围</div>
            <DatePicker.RangePicker
              value={dates as [Dayjs, Dayjs]}
              onChange={(vals) => {
                if (vals && vals[0] && vals[1]) {
                  setDates([vals[0], vals[1]]);
                } else {
                  setDates([null, null]);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>时间聚合尺度</div>
            <Select
              value={aggLevel}
              options={AGG_OPTIONS}
              onChange={setAggLevel}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>主轴(Y1)样式</div>
            <Select
              value={y1Type}
              options={CHART_TYPE_OPTIONS}
              onChange={setY1Type}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>次轴(Y2)样式</div>
            <Select
              value={y2Type}
              options={CHART_TYPE_OPTIONS}
              onChange={setY2Type}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>&nbsp;</div>
            <Button
              type="primary"
              icon={<LineChartOutlined />}
              onClick={() => refetch()}
              disabled={!canQuery}
              style={{ width: '100%' }}
            >
              执行耦合分析与对齐
            </Button>
          </Col>
        </Row>

        {metricsError && (
          <Alert
            message={metricsError instanceof Error ? metricsError.message : '指标列表加载失败'}
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}

        {/* Metric selectors */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Y1 主轴指标</div>
            <Segmented
              options={TAG_OPTIONS}
              value={y1Tag}
              onChange={(v) => setY1Tag(v as string)}
              style={{ marginBottom: 8 }}
            />
            <Select
              mode="multiple"
              value={y1Metrics}
              options={y1Options.map((n) => ({ value: n, label: n }))}
              onChange={setY1Metrics}
              style={{ width: '100%' }}
              placeholder="选择 Y1 指标"
              showSearch
            />
          </Col>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Y2 次轴指标</div>
            <Segmented
              options={TAG_OPTIONS}
              value={y2Tag}
              onChange={(v) => setY2Tag(v as string)}
              style={{ marginBottom: 8 }}
            />
            <Select
              mode="multiple"
              value={y2Metrics}
              options={y2Options.map((n) => ({ value: n, label: n }))}
              onChange={setY2Metrics}
              style={{ width: '100%' }}
              placeholder="选择 Y2 指标"
              showSearch
            />
          </Col>
        </Row>

        {unitError && (
          <Alert message={unitError} type="error" showIcon style={{ marginTop: 12 }} />
        )}
      </div>

      {/* Chart */}
      <ChartCard
        title="耦合分析图表"
        option={chartOption}
        loading={isLoading}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        height={550}
      />

      {/* CSV Export */}
      {wbData && (
        <div style={{ marginTop: 16 }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportCsv}>
            导出当前页面对齐数据 (CSV)
          </Button>
        </div>
      )}
    </PageContainer>
  );
};

export default WorkbenchPage;
